import { useState } from "react";
import type {
    CellStyle,
    ColDef,
    ICellRendererParams,
} from "ag-grid-community";
import { Link } from "react-router-dom";

// Shared palette / helpers mirror the COT analysis grid (AgGridGrid.tsx) so the
// portfolio table reads consistently with the rest of the app.
const GREEN = "#198754";
const RED = "#dc3545";
const MUTED = "#6c757d";

const TABULAR = { fontVariantNumeric: "tabular-nums" as const };
const DAY_MS = 86_400_000;

export type Direction = "long" | "short";

export const DIRECTION_COLOR: Record<Direction, string> = {
    long: GREEN,
    short: RED,
};

export const directionOf = (
    netPosition: number | null | undefined,
): Direction | null =>
    netPosition == null || netPosition === 0
        ? null
        : netPosition > 0
          ? "long"
          : "short";

// Shared ▲/▼ marker so the grid's market names and the detail pane header read
// the same way.
export const DirectionMark = ({
    direction,
    fontSize = "0.7rem",
}: {
    direction: Direction;
    fontSize?: string;
}) => (
    <span style={{ color: DIRECTION_COLOR[direction], fontSize }}>
        {direction === "long" ? "\u25B2" : "\u25BC"}
    </span>
);

const fmtInt = (v: number | null | undefined) =>
    v == null ? "—" : Math.round(v).toLocaleString();

const fmtSignedInt = (v: number | null | undefined) =>
    v == null ? "—" : (v > 0 ? "+" : "") + Math.round(v).toLocaleString();

const fmtPct = (v: number | null | undefined, digits = 1) =>
    v == null ? "—" : v.toFixed(digits) + "%";

const fmtSignedPct = (v: number | null | undefined, digits = 1) =>
    v == null ? "—" : (v > 0 ? "+" : "") + v.toFixed(digits) + "%";

const fmtShortDate = (d?: string | null) =>
    d
        ? new Date(d).toLocaleDateString(undefined, {
              month: "2-digit",
              day: "2-digit",
              year: "2-digit",
          })
        : null;

/**
 * Row shape consumed by the portfolio grid. Fields marked "placeholder" are not
 * yet wired to real data — they hold mock/derived values until the backing
 * endpoints exist.
 */
export interface PortfolioRow {
    // Stable identity for the row, used as the grid row id and to look up the
    // selected market's history for the detail chart.
    root: string;
    name: string;

    // MM Net column
    netPosition: number;
    netChange: number | null;
    netChangePct: number | null;

    // Spread Price Action column (front/back roll contracts + spread symbol)
    spreadFrom: string | null;
    spreadTo: string | null;
    spreadSymbol: string | null;

    // Roll column
    daysToRoll: number | null;
    rollStartDate: string | null;
    rollEndDate: string | null;

    // Historical pct. of range column
    relativePct: number | null;
    relativePctile: number | null; // placeholder

    // MM % of OI column
    mmPctOi: number | null;
    mmPctOiPctile: number | null; // placeholder

    // Sizing reference (placeholder) + confidence stars
    stars: number;
}

export interface PortfolioGridContext {
    isCurrentlyTrading: (root: string) => boolean;
    onCurrentlyTradingChange: (root: string, trading: boolean) => void;
}

// Wrapper that stacks a primary value over a muted secondary line, centered to
// match the drawn mockup.
const StackedCell = ({
    primary,
    secondary,
    primaryColor,
    primaryBold,
}: {
    primary: React.ReactNode;
    secondary?: React.ReactNode;
    primaryColor?: string;
    primaryBold?: boolean;
}) => (
    <div
        style={{
            lineHeight: 1.2,
            textAlign: "center",
            ...TABULAR,
        }}
    >
        <div
            style={{
                color: primaryColor,
                fontWeight: primaryBold ? 600 : undefined,
            }}
        >
            {primary}
        </div>
        {secondary != null && (
            <div style={{ fontSize: "0.7rem", color: MUTED }}>{secondary}</div>
        )}
    </div>
);

// Mirrors AgGridGrid's rollCellStyle: past roll-end is red, inside the roll
// window is amber, otherwise green/red by days-until.
const rollColor = (row: PortfolioRow): string => {
    const v = row.daysToRoll;
    if (v == null) return MUTED;
    if (row.rollEndDate) {
        const daysToEnd = Math.floor(
            (new Date(row.rollEndDate).getTime() - Date.now()) / DAY_MS,
        );
        if (daysToEnd < 0) return RED;
        if (v <= 0) return "#fd7e14";
    }
    return v < 0 ? RED : GREEN;
};

export const MarketNameCell = (p: ICellRendererParams<PortfolioRow>) => {
    if (!p.data) return null;
    const { name, root, netPosition } = p.data;
    const direction = directionOf(netPosition);
    return (
        <div style={{ lineHeight: 1.2, minWidth: 0 }}>
            <div className="fw-semibold text-truncate" title={name}>
                {name}
                {direction && (
                    <>
                        {" "}
                        <DirectionMark direction={direction} />
                    </>
                )}
            </div>
            <div style={{ fontSize: "0.7rem", color: MUTED, ...TABULAR }}>
                {root}
                {direction && ` \u00b7 ${direction}`}
            </div>
        </div>
    );
};

export const MMNetCell = (p: ICellRendererParams<PortfolioRow>) => {
    if (!p.data) return null;
    const { netPosition, netChange, netChangePct } = p.data;
    return (
        <StackedCell
            primary={fmtInt(netPosition)}
            secondary={
                netChange != null ? (
                    <span
                        style={{ color: netChange >= 0 ? GREEN : RED }}
                    >
                        {fmtSignedInt(netChange)}
                        {netChangePct != null &&
                            ` (${fmtSignedPct(netChangePct)})`}
                    </span>
                ) : undefined
            }
        />
    );
};

export const SpreadPriceActionCell = (
    p: ICellRendererParams<PortfolioRow>,
) => {
    if (!p.data) return null;
    const { spreadFrom, spreadTo, spreadSymbol } = p.data;
    const legs =
        spreadFrom && spreadTo ? `${spreadFrom} \u2192 ${spreadTo}` : "—";
    return (
        <StackedCell
            primary={legs}
            secondary={spreadSymbol ?? undefined}
        />
    );
};

export const RollCell = (p: ICellRendererParams<PortfolioRow>) => {
    if (!p.data) return null;
    const v = p.data.daysToRoll;
    const start = fmtShortDate(p.data.rollStartDate);
    const end = fmtShortDate(p.data.rollEndDate);
    return (
        <StackedCell
            primary={
                v != null
                    ? `${Math.abs(v)} Days ${v > 0 ? "Until" : "Past"}`
                    : "N/A"
            }
            primaryColor={rollColor(p.data)}
            primaryBold
            secondary={start || end ? `${start ?? "?"} - ${end ?? "?"}` : undefined}
        />
    );
};

export const RelativeRangeCell = (p: ICellRendererParams<PortfolioRow>) => {
    if (!p.data) return null;
    const { relativePct, relativePctile } = p.data;
    const color =
        relativePct == null || relativePct === 0
            ? MUTED
            : relativePct > 0
              ? GREEN
              : RED;
    return (
        <StackedCell
            primary={fmtSignedPct(relativePct)}
            primaryColor={color}
            secondary={`${relativePctile != null ? relativePctile.toFixed(0) : "—"}%-ile of all reports`}
        />
    );
};

export const MMPctOiCell = (p: ICellRendererParams<PortfolioRow>) => {
    if (!p.data) return null;
    const { mmPctOi, mmPctOiPctile } = p.data;
    return (
        <StackedCell
            primary={fmtPct(mmPctOi)}
            secondary={`${mmPctOiPctile != null ? mmPctOiPctile.toFixed(0) : "—"}%-ile of all reports`}
        />
    );
};

export const CurrentlyTradingCell = (
    p: ICellRendererParams<PortfolioRow, boolean, PortfolioGridContext>,
) => {
    if (!p.data) return null;
    const { root, name } = p.data;
    // Local checked state so the box flips on this click. Parent rowData is
    // not rebuilt for trading toggles — that was the source of the lag.
    const [checked, setChecked] = useState(
        () => p.context?.isCurrentlyTrading(root) ?? false,
    );
    return (
        <div
            className="d-flex justify-content-center align-items-center h-100"
            // Keep the checkbox from also selecting the row for the detail pane.
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            <input
                type="checkbox"
                className="form-check-input m-0"
                checked={checked}
                onChange={(e) => {
                    const next = e.target.checked;
                    setChecked(next);
                    p.context?.onCurrentlyTradingChange(root, next);
                }}
                aria-label={`Currently trading ${name}`}
                style={{
                    cursor: "pointer",
                    width: "1.35rem",
                    height: "1.35rem",
                }}
            />
        </div>
    );
};

const StarsCell = (p: ICellRendererParams<PortfolioRow>) => (
    <div className="d-flex justify-content-center align-items-center gap-1 h-100">
        {Array.from({ length: 5 }).map((_, index) => (
            <i
                key={index}
                className={`bi ${
                    index < (p.value ?? 0)
                        ? "bi-star-fill text-warning"
                        : "bi-star text-secondary"
                }`}
            />
        ))}
    </div>
);

const DetailsCell = (p: ICellRendererParams<PortfolioRow>) => {
    if (!p.data) return null;
    return (
        <Link
            to={`/COT-analysis/${p.data.root}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary py-0 px-2 d-inline-flex align-items-center gap-1"
            style={{ fontSize: "0.75rem" }}
            title="Opens in a new tab"
            onClick={(e) => e.stopPropagation()}
        >
            Details
            <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
        </Link>
    );
};

const centeredHeader: CellStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

/**
 * Column definitions for the COT portfolio grid, matching the drawn mockup:
 * Currently Trading · Market Name · MM Net · Spread Price Action · Roll ·
 * Historical pct. of range · MM % of OI · Sizing Reference · Confidence · Details.
 */
export const portfolioColumnDefs: ColDef<PortfolioRow>[] = [
    {
        colId: "currentlyTrading",
        headerName: "Currently Trading",
        valueGetter: (params) =>
            params.data
                ? (params.context?.isCurrentlyTrading(params.data.root) ?? false)
                : false,
        headerTooltip: "Markets you are currently trading",
        width: 92,
        minWidth: 84,
        maxWidth: 110,
        pinned: "left",
        wrapHeaderText: true,
        autoHeaderHeight: true,
        cellStyle: centeredHeader,
        cellRenderer: CurrentlyTradingCell,
        // Clicks in this cell should not change the drill-down selection.
        onCellClicked: (event) => {
            event.event?.stopPropagation();
        },
    },
    {
        field: "name",
        headerName: "Market Name",
        flex: 2,
        minWidth: 110,
        cellStyle: { ...centeredHeader, justifyContent: "flex-start" },
        cellRenderer: MarketNameCell,
    },
    {
        field: "netPosition",
        headerName: "MM Net",
        flex: 1.2,
        minWidth: 100,
        cellStyle: centeredHeader,
        cellRenderer: MMNetCell,
    },
    {
        colId: "spreadPriceAction",
        headerName: "Spread Price Action",
        flex: 1.2,
        minWidth: 110,
        sortable: false,
        cellStyle: centeredHeader,
        cellRenderer: SpreadPriceActionCell,
    },
    {
        field: "daysToRoll",
        headerName: "Roll",
        flex: 1.2,
        minWidth: 120,
        sortingOrder: ["desc", "asc", null],
        cellStyle: centeredHeader,
        cellRenderer: RollCell,
    },
    {
        field: "relativePct",
        headerName: "Historical pct. of range",
        flex: 1.2,
        minWidth: 120,
        cellStyle: centeredHeader,
        cellRenderer: RelativeRangeCell,
        comparator: (
            a: number | null,
            b: number | null,
            _nodeA,
            _nodeB,
            isDescending: boolean,
        ) => {
            if (a == null && b == null) return 0;
            const nullWeight = isDescending ? -1 : 1;
            if (a == null) return nullWeight;
            if (b == null) return -nullWeight;
            const diff = Math.abs(a) - Math.abs(b);
            if (diff !== 0) return diff;
            return a - b;
        },
    },
    {
        field: "mmPctOi",
        headerName: "MM % of OI",
        flex: 1.2,
        minWidth: 120,
        cellStyle: centeredHeader,
        cellRenderer: MMPctOiCell,
    },
    {
        colId: "sizingReference",
        headerName: "Sizing Reference",
        flex: 1,
        minWidth: 100,
        sortable: false,
        cellStyle: { ...centeredHeader, color: MUTED },
        cellRenderer: () => <span>—</span>,
    },
    {
        field: "stars",
        headerName: "Confidence",
        flex: 1,
        minWidth: 110,
        hide: true,
        cellStyle: centeredHeader,
        cellRenderer: StarsCell,
    },
    {
        colId: "details",
        headerName: "",
        width: 110,
        pinned: "right",
        sortable: false,
        resizable: false,
        cellStyle: centeredHeader,
        cellRenderer: DetailsCell,
    },
];
